import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useThemeType } from '@gravity-ui/uikit';

export const paletteDefaults = {
    light: { canvas: '#eeede6', surface: '#f4f3ed', ink: '#22241f', accent: '#363e2d' },
    dark: { canvas: '#1d1f1b', surface: '#252821', ink: '#eeede6', accent: '#c1cda9' },
};
export type Palette = typeof paletteDefaults.light;
type SavedPalettes = Partial<Record<'light' | 'dark', Palette>>;
const key = 'locomotive.palette.v1';
function readPalettes(): SavedPalettes {
    try {
        const saved = JSON.parse(localStorage.getItem(key) || '{}');
        const result: SavedPalettes = {};
        for (const theme of ['light', 'dark'] as const) {
            if (Object.keys(paletteDefaults.light).every(name => /^#[0-9a-f]{6}$/i.test(saved?.[theme]?.[name]))) {
                result[theme] = Object.fromEntries(Object.keys(paletteDefaults.light).map(name => [name, saved[theme][name]])) as Palette;
            }
        }
        return result;
    } catch { return {}; }
}
const PaletteContext = createContext<{
    palette: Palette; update: (palette: Palette | null) => void; storageError: boolean;
}>({ palette: paletteDefaults.light, update: () => {}, storageError: false });
export const usePalette = () => useContext(PaletteContext);

export function PaletteProvider({ children }: { children: ReactNode }) {
    const theme = useThemeType() === 'dark' ? 'dark' : 'light';
    const [saved, setSaved] = useState(readPalettes);
    const [storageError, setStorageError] = useState(false);
    const palette = saved[theme] || paletteDefaults[theme];
    useEffect(() => {
        const sync = (event: StorageEvent) => { if (event.key === key || event.key === null) setSaved(readPalettes()); };
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, []);
    useEffect(() => {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palette.canvas);
    }, [palette]);
    const update = (next: Palette | null) => {
        const value = { ...saved };
        if (next) value[theme] = next;
        else delete value[theme];
        setSaved(value);
        try { localStorage.setItem(key, JSON.stringify(value)); setStorageError(false); }
        catch { setStorageError(true); }
    };
    return <PaletteContext.Provider value={{ palette, update, storageError }}>
        {saved[theme] && <style>{`.g-root.g-root {
            --canvas: ${palette.canvas}; --surface: ${palette.surface}; --ink: ${palette.ink}; --accent: ${palette.accent};
            --muted: color-mix(in srgb, var(--ink) 72%, var(--canvas));
            --line: color-mix(in srgb, var(--ink) 24%, var(--canvas));
            --accent-soft: color-mix(in srgb, var(--accent) 12%, var(--surface));
            --special: var(--accent); --special-soft: var(--accent-soft);
            --g-color-base-brand: var(--accent); --g-color-base-brand-hover: var(--ink);
        }`}</style>}
        {children}
    </PaletteContext.Provider>;
}
